import React from "react";

const businessConsultant = {
  heroTitleEm: "Certified Business Consultant",
  programmeDetails: [
    { label: "School", value: "Faculty of Business & Management" },
    { label: "Start Date", value: "October 2026" },
    { label: "Certificate", value: "Professional Certificate" },
    { label: "Fee", value: "\u20ac750" },
    { label: "Credits", value: "N/A" },
    { label: "Language", value: "English" },
    { label: "Duration", value: "6-12 Months" },
    { label: "Mode", value: "Distance Learning" },
  ],
  overviewParagraphs: [
    "Welcome to The Certified Business Consultant Programme, which offers a unique opportunity to earn the esteemed Certified Business Consultant designation.",
    "The programme equips professionals with advanced expertise in strategic consulting, financial analysis, marketing, and global business dynamics. Designed for career progression, it fosters critical thinking, research-based decision-making, and innovative problem-solving in complex business environments.",
    "Combining academic rigour with practical application, this programme prepares graduates to become thought leaders in business consultancy and management.",
  ],
  overviewImageAlt: "Business Consultancy",
  highlights: [
    {
      title: "Globally Recognised Certification",
      text: "Earn a globally recognised certification to boost your professional credentials.",
      icon: "globe",
    },
    {
      title: "Flexible Learning",
      text: "Participate in 750 hours of total learning with pacing flexibility for maximum flexibility.",
      icon: "clock",
    },
    {
      title: "Networking Opportunities",
      text: "Connect with a global cohort of professionals and industry experts to expand your network.",
      icon: "people",
    },
    {
      title: "Practical Projects",
      text: "Apply skills to real-world scenarios through case studies, workshops, and a capstone project.",
      icon: "clipboard",
    },
    {
      title: "Career Advancement",
      text: "Gain recognised qualifications that unlock pathways to degree programmes and career growth.",
      icon: "bolt",
    },
  ],
  modules: [
    {
      title: "Foundations of Business Consultancy",
      desc: "Explore the core principles of business consultancy, including consulting frameworks, client relationship management, and the consulting lifecycle from engagement to delivery.",
    },
    {
      title: "Advanced Strategic Management",
      desc: "Develop advanced strategic thinking skills, including competitive analysis, corporate strategy formulation, and strategic implementation in diverse organisational contexts.",
    },
    {
      title: "Financial Analysis for Consultants",
      desc: "Master financial analysis techniques essential for consultants, including financial statement analysis, budgeting, forecasting, and investment appraisal for client advisory.",
    },
    {
      title: "Marketing & Branding for Consultants",
      desc: "Learn to develop marketing strategies and brand positioning for consulting practices and client organisations, covering digital marketing, market research, and brand management.",
    },
    {
      title: "Ethics & Legal Compliance in Consultancy",
      desc: "Examine ethical standards, professional codes of conduct, data protection regulations, and legal compliance frameworks relevant to business consultancy practice.",
    },
    {
      title: "Advanced Data Analytics & Technology",
      desc: "Explore how data analytics, business intelligence tools, and emerging technologies support evidence-based consulting recommendations and digital transformation.",
    },
    {
      title: "Training & Mentorship in Consultancy",
      desc: "Develop skills in training delivery, mentorship frameworks, and knowledge transfer methodologies essential for consultant-led capacity building engagements.",
    },
    {
      title: "Global Business & Entrepreneurship",
      desc: "Analyse global business trends, international market entry strategies, and entrepreneurial approaches to building and scaling consulting practices across borders.",
    },
    {
      title: "Capstone Project",
      desc: "Apply accumulated knowledge and skills to a comprehensive real-world consulting project, demonstrating strategic analysis, problem-solving, and professional presentation capabilities.",
    },
  ],
  careersIntro: "Graduates are prepared for leadership roles in business consultancy",
  careersIconKey: "briefcase",
  careers: [
    { title: "Business Consultant", text: "Provide expert advice to businesses to improve performance and efficiency." },
    { title: "Financial Analyst", text: "Analyse financial data and provide insights to support strategic decision-making." },
    { title: "Strategic Manager", text: "Develop and implement strategies to achieve long-term organisational goals." },
    { title: "Marketing Consultant", text: "Design and execute marketing strategies to enhance brand presence and client acquisition." },
    { title: "Corporate Trainer", text: "Deliver professional development programmes and workshops to enhance employee skills." },
    { title: "International Business Advisor", text: "Provide guidance on global business practices, cross-border trade, and market entry strategies." },
  ],
  keyAttractions: [
    { title: "Hands-On Leadership Workshops", text: "Engage in practical sessions that build your leadership skills." },
    { title: "Eligibility for Summer School Programmes in Europe", text: "Exclusive access to immersive summer programmes." },
    {
      title: "Professional Certification",
      text: "Earn a professional certificate issued by ESBD, a Malta-based higher education institution. This programme is delivered as a Continuing Professional Development (CPD) certificate, recognised for career advancement and professional growth.",
    },
    {
      title: "Networking Opportunities",
      text: "Build lasting connections with peers, mentors, and global professionals.",
    },
    { title: "Pathway to a Degree", text: "Opportunity to articulate into advanced degree programmes." },
  ],
  ctaHeading: "Ready to Launch Your Consulting Career?",
  ctaParagraph: "Enrol today and become a certified business consultant.",
  iconMap: {
    globe: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="1.5" />
        <path d="M2 12h20" stroke="white" strokeWidth="1.5" />
      </svg>
    ),
    clock: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path
          d="M12 6v6l4 2"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="1.5" />
      </svg>
    ),
    people: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path
          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2M15 7a3 3 0 11-6 0 3 3 0 016 0z"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),
    clipboard: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),
    bolt: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path
          d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"
          stroke="white"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      </svg>
    ),
    briefcase: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <path
          d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
};

const dataAnalyst = {
  heroTitleEm: "Certified Data Analyst",
  programmeDetails: [
    { label: "School", value: "Faculty of Computer Science" },
    { label: "Start Date", value: "October 2026" },
    { label: "Certificate", value: "Professional Certificate" },
    { label: "Fee", value: "\u20ac750" },
    { label: "Credits", value: "N/A" },
    { label: "Language", value: "English" },
    { label: "Duration", value: "6 Months | 2 Terms" },
    { label: "Mode", value: "Distance Learning" },
  ],
  overviewParagraphs: [
    "The programme develops advanced analytical capabilities, fostering a deep understanding of data transformation techniques, statistical analysis, and data storytelling.",
    "Students will engage with cutting-edge technologies and methodologies while cultivating critical thinking, problem-solving, and ethical understanding essential to global data industries.",
    "Designed to bridge academic knowledge and professional practice, the programme empowers students to lead data-driven innovation across sectors.",
  ],
  overviewImageAlt: "Data Analytics",
  highlights: [
    {
      title: "Globally Recognised Certification",
      text: "Earn a globally recognised certification to boost your professional credentials.",
      icon: "globe",
    },
    {
      title: "Flexible Learning",
      text: "Participate in 450 hours of total learning with pacing flexibility for maximum flexibility.",
      icon: "clock",
    },
    {
      title: "Networking Opportunities",
      text: "Connect with a global cohort of professionals and industry experts to expand your network.",
      icon: "people",
    },
    {
      title: "Practical Projects",
      text: "Apply skills to real-world scenarios through case studies, workshops, and a capstone project.",
      icon: "clipboard",
    },
    {
      title: "Career Advancement",
      text: "Gain recognised qualifications that unlock pathways to degree programmes and career growth.",
      icon: "bolt",
    },
    {
      title: "Expert Faculty",
      text: "Learn from experienced practitioners and academics with industry-relevant expertise.",
      icon: "cap",
    },
  ],
  modules: [
    {
      title: "Introduction to Data Analytics",
      desc: "Explore the fundamentals of data analytics, including key concepts, tools, and the role of data in modern decision-making across industries.",
    },
    {
      title: "Foundations of Data Analysis",
      desc: "Learn core data analysis techniques including data cleaning, transformation, exploratory analysis, and working with structured datasets.",
    },
    {
      title: "Advanced Statistical Methods",
      desc: "Develop proficiency in statistical modelling, hypothesis testing, regression analysis, and predictive analytics for business applications.",
    },
    {
      title: "Data Ethics & Governance",
      desc: "Examine ethical frameworks, data privacy regulations (GDPR), responsible AI practices, and governance standards for data-driven organisations.",
    },
    {
      title: "Data Visualisation & Communication",
      desc: "Master the art of presenting data insights through compelling visualisations using tools like Tableau, Power BI, and Python libraries.",
    },
    {
      title: "Applied Data Analytics",
      desc: "Apply analytical skills to real-world business problems through capstone projects, case studies, and portfolio-building exercises.",
    },
  ],
  careersIntro: "Graduates are prepared for roles across data-driven organisations",
  careersIconKey: "chart",
  careers: [
    { title: "Data Analyst", text: "Analyse and interpret complex datasets to provide insights for decision-making." },
    { title: "Business Intelligence Analyst", text: "Help organisations use data to enhance business performance and strategy." },
    { title: "Data Visualisation Specialist", text: "Create clear, effective visualisations to communicate data-driven insights." },
    { title: "Data Consultant", text: "Provide expert advice to organisations on how to optimise their data and analytics." },
    { title: "Market Research Analyst", text: "Use data to understand consumer behaviour, market trends, and customer preferences." },
    { title: "Operations Analyst", text: "Analyse organisational efficiency to identify inefficiencies and recommend improvements." },
    { title: "Financial Analyst", text: "Apply data analysis techniques to support financial decision-making and forecasting." },
  ],
  keyAttractions: [
    { title: "Hands-On Leadership Workshops", text: "Engage in practical sessions that build your leadership skills." },
    { title: "Eligibility for Summer School Programmes in Europe", text: "Exclusive access to immersive summer programmes." },
    {
      title: "Professional Certification",
      text: "Earn a professional certificate issued by ESBD, a Malta-based higher education institution. This programme is delivered as a Continuing Professional Development (CPD) certificate, recognised for career advancement and professional growth.",
    },
    { title: "Networking Opportunities", text: "Build lasting connections with peers, mentors, and global professionals." },
    { title: "Pathway to a Degree", text: "Opportunity to articulate into advanced degree programmes." },
  ],
  ctaHeading: "Ready to Start Your Data Career?",
  ctaParagraph: "Enrol today and unlock your potential in data analytics.",
  iconMap: {
    globe: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="1.5" />
        <path d="M2 12h20" stroke="white" strokeWidth="1.5" />
      </svg>
    ),
    clock: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path
          d="M12 6v6l4 2"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="1.5" />
      </svg>
    ),
    people: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path
          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2M15 7a3 3 0 11-6 0 3 3 0 016 0z"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),
    clipboard: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),
    bolt: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path
          d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"
          stroke="white"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      </svg>
    ),
    cap: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path
          d="M12 14l9-5-9-5-9 5 9 5zm0 0v7"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    chart: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <path
          d="M16 8v8m-4-5v5m-4-2v2m-2 4h16a2 2 0 002-2V5a2 2 0 00-2-2H6a2 2 0 00-2 2v14a2 2 0 002 2z"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
};

const executiveCareerCoach = {
  heroTitleEm: "Certified Executive Career Coach",
  programmeDetails: [
    { label: "School", value: "Faculty of Business & Management" },
    { label: "Start Date", value: "October 2026" },
    { label: "Certificate", value: "Professional Certificate" },
    { label: "Fee", value: "\u20ac750" },
    { label: "Credits", value: "N/A" },
    { label: "Language", value: "English" },
    { label: "Duration", value: "6-12 Months" },
    { label: "Mode", value: "Distance Learning" },
  ],
  overviewParagraphs: [
    "Elevate your career with the Certified Executive Career Coach Programme. This programme, offered by ESBD, awards the distinguished Certified Executive Career Coach designation, equipping you to excel as a leader in career coaching and professional development.",
    "The programme develops advanced expertise in career coaching, combining rigorous theoretical frameworks with practical application. Participants will master coaching methodologies, ethical practices, and innovative strategies to support professionals in achieving career success.",
    "Through research, real learning, critical analysis, and leadership development, this programme equips graduates to become transformative leaders in career coaching, capable of driving organisational and individual growth in diverse professional contexts.",
  ],
  overviewImageAlt: "Executive Career Coaching",
  highlights: [
    {
      title: "Globally Recognised Certification",
      text: "Earn a globally recognised certification to boost your professional credentials.",
      icon: "globe",
    },
    {
      title: "Flexible Learning",
      text: "Participate in 750 hours of total learning with pacing flexibility for maximum flexibility.",
      icon: "clock",
    },
    {
      title: "Networking Opportunities",
      text: "Connect with a global cohort of professionals and industry experts to expand your network.",
      icon: "people",
    },
    {
      title: "Practical Projects",
      text: "Apply skills to real-world scenarios through case studies, workshops, and a capstone project.",
      icon: "clipboard",
    },
    {
      title: "Career Advancement",
      text: "Gain recognised qualifications that unlock pathways to degree programmes and career growth.",
      icon: "bolt",
    },
  ],
  modules: [
    {
      title: "Foundations of Career Coaching",
      desc: "Explore the core principles of career coaching, including coaching models, counselling techniques, validated career assessment instruments, and the role of a career coach in guiding clients through professional transitions and development.",
    },
    {
      title: "Leadership Coaching Strategies",
      desc: "Develop advanced coaching strategies for leadership development, executive presence, emotional intelligence, and high-performance team dynamics in corporate and organisational settings.",
    },
    {
      title: "Goal Setting & Career Planning",
      desc: "Master structured goal-setting frameworks and career planning methodologies to help clients create actionable career development roadmaps aligned with their aspirations and market opportunities.",
    },
    {
      title: "Reflective Practice & Research-Based Coaching",
      desc: "Develop critical self-awareness through structured reflective practice and evidence-based coaching methodologies. Apply research findings to design data-informed coaching interventions for measurable client outcomes.",
    },
    {
      title: "Coaching Ethics, Standards & Professional Development",
      desc: "Examine global coaching standards, professional ethics, cultural competence, ethical decision-making frameworks, and continuous professional growth aligned with international coaching bodies.",
    },
    {
      title: "Advanced Facilitation & Training Techniques",
      desc: "Build expertise in group facilitation, workshop design, interactive training delivery, and knowledge transfer methodologies for corporate and individual coaching contexts.",
    },
    {
      title: "Capstone: Applied Coaching Practice",
      desc: "Apply accumulated knowledge and skills to a comprehensive real-world coaching engagement, demonstrating strategic coaching design, client management, reflective analysis, and professional presentation capabilities.",
    },
  ],
  careersIntro:
    "Graduates are prepared for leadership roles in coaching and leadership development",
  careersIconKey: "briefcase",
  careers: [
    {
      title: "Senior Leadership Roles",
      text: "Upon completion of this programme, you will have the skills to pursue senior leadership roles in corporate organisations.",
    },
    {
      title: "Gender Advocacy Specialist",
      text: "Graduates will be well-positioned to advocate for gender equality in corporate and non-profit environments.",
    },
    { title: "Executive Career Coach", text: "Launch or grow a professional coaching practice serving executives and senior professionals." },
    { title: "Corporate Training Manager", text: "Lead organisational learning and development programmes in corporate environments." },
    { title: "HR Development Consultant", text: "Provide strategic human resource development consulting to organisations." },
    { title: "Leadership Development Facilitator", text: "Design and deliver leadership development programmes for emerging and established leaders." },
  ],
  keyAttractions: [
    { title: "Hands-On Leadership Workshops", text: "Engage in practical sessions that build your leadership skills." },
    { title: "Eligibility for Summer School Programmes in Europe", text: "Exclusive access to immersive summer programmes." },
    { title: "Professional Certification", text: "Earn a professional certificate issued by ESBD, a Malta-based higher education institution. This programme is delivered as a Continuing Professional Development (CPD) certificate, recognised for career advancement and professional growth." },
    { title: "Networking Opportunities", text: "Build lasting connections with peers, mentors, and global professionals." },
    { title: "Pathway to a Degree", text: "Opportunity to articulate into advanced degree programmes." },
  ],
  ctaHeading: "Ready to Elevate Your Coaching Career?",
  ctaParagraph: "Enrol today and become a certified executive career coach.",
  iconMap: {
    globe: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="1.5" />
        <path d="M2 12h20" stroke="white" strokeWidth="1.5" />
      </svg>
    ),
    clock: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path
          d="M12 6v6l4 2"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="1.5" />
      </svg>
    ),
    people: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path
          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2M15 7a3 3 0 11-6 0 3 3 0 016 0z"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),
    clipboard: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),
    bolt: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path
          d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"
          stroke="white"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      </svg>
    ),
    briefcase: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <path
          d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
};

const agileThoughtLeader = {
  heroTitleEm: "Certified Agile Thought Leader Diploma",
  programmeDetails: [
    { label: "School", value: "Faculty of Business & Management" },
    { label: "Start Date", value: "October 2026" },
    { label: "Certificate", value: "Professional Certificate" },
    { label: "Fee", value: "\u20ac750" },
    { label: "Credits", value: "N/A" },
    { label: "Language", value: "English" },
    { label: "Duration", value: "6-8 Months" },
    { label: "Mode", value: "Distance Learning" },
  ],
  overviewParagraphs: [
    "The Certified Agile Thought Leader Diploma Programme develops advanced leadership capabilities through a competency-based, career-driven approach to leadership development, preparing you for senior roles across agile and innovative organisations.",
    "This programme develops advanced leadership capabilities through agile methodologies, strategic thinking, and innovation management. Participants will master adaptive leadership frameworks, design thinking, and evidence-based decision-making to lead effectively in rapidly changing environments.",
    "Combining academic rigour with practical application, this programme prepares graduates to become agile thought leaders capable of driving transformation, inspiring teams, and navigating complexity across sectors.",
  ],
  overviewImageAlt: "Agile Leadership",
  highlights: [
    {
      title: "Professional Diploma Qualification",
      text: "Graduate with the internationally recognised Certified Agile Thought Leader Diploma, a professionally valued qualification.",
      icon: "globe",
    },
    {
      title: "Progression to Master\u2019s Degree",
      text: "Successful graduates may be eligible for progression pathways into advanced postgraduate programmes, accelerating their academic and professional journey.",
      icon: "clock",
    },
    {
      title: "Global Webinars",
      text: "Engage in expert-led webinars with industry leaders shaping the world.",
      icon: "people",
    },
    {
      title: "Interactive Online Tutorials",
      text: "Benefit from comprehensive online tutorials with the programme leader, updated weekly and monthly.",
      icon: "clipboard",
    },
    {
      title: "Practical Engagement",
      text: "Work on practical group assignments and a leadership Capstone Project to apply your skills.",
      icon: "bolt",
    },
    {
      title: "Mentorship Connect",
      text: "Receive direct guidance and mentorship from seasoned professionals throughout the programme.",
      icon: "cap",
    },
  ],
  modules: [
    {
      title: "Agile Leadership & Emerging Trends",
      desc: "Explore contemporary agile leadership models, emerging trends in organisational leadership, and how adaptive thinking drives innovation in complex, fast-paced environments.",
    },
    {
      title: "Collaborative Leadership & Team Dynamics",
      desc: "Develop skills in collaborative leadership, team facilitation, conflict resolution, and building high-performing teams within agile organisational structures.",
    },
    {
      title: "Strategic Thinking & Decision-Making",
      desc: "Master strategic analysis frameworks, scenario planning, and evidence-based decision-making methodologies for leaders operating under uncertainty.",
    },
    {
      title: "Leading in the Digital Age",
      desc: "Examine how digital transformation, AI, and emerging technologies are reshaping leadership practice, organisational design, and stakeholder engagement.",
    },
    {
      title: "Innovation Management & Design Thinking",
      desc: "Apply design thinking and innovation management frameworks to develop creative solutions, lead product development, and drive organisational change.",
    },
    {
      title: "Agile Values, Sustainability & Ethical Leadership",
      desc: "Explore the intersection of agile values, sustainability principles, and ethical leadership, developing a responsible approach to decision-making and organisational governance.",
    },
    {
      title: "Capstone: Applied Agile Leadership Project",
      desc: "Apply accumulated knowledge to a comprehensive real-world leadership project, demonstrating strategic agility, stakeholder engagement, research capability, and measurable impact.",
    },
  ],
  careersIntro:
    "Graduates are prepared for leadership roles across agile and innovative organisations",
  careersIconKey: "briefcase",
  careers: [
    { title: "Leadership Development Specialist", text: "Design and deliver leadership development programmes within organisations, specialising in agile and adaptive leadership approaches." },
    { title: "Agile Project Manager", text: "Lead cross-functional teams to implement agile methodologies, ensuring project success through intelligent planning, continuous improvement, and effective collaboration." },
    { title: "Change Management Consultant", text: "Help organisations navigate change effectively, with a focus on leadership alignment, stakeholder engagement, and organisational culture." },
    { title: "Corporate Strategist", text: "Develop and execute strategic initiatives that drive business growth and innovation, using the skills in strategic thinking and adaptive leadership gained from the programme." },
    { title: "Leadership Coach or Mentor", text: "Provide one-on-one coaching or mentoring to emerging or seasoned leaders, helping them navigate challenges and develop their leadership potential in both corporate and non-profit environments." },
    { title: "Innovation & Transformation Director", text: "Lead organisational innovation programmes, digital transformation initiatives, and culture change strategies across public and private sector organisations." },
  ],
  keyAttractions: [
    { title: "Hands-On Leadership Workshops", text: "Engage in practical sessions that build your leadership skills." },
    { title: "Eligibility for Summer School Programmes in Europe", text: "Exclusive access to immersive summer programmes." },
    { title: "Professional Certification", text: "Earn the Certified Agile Thought Leader Diploma issued by ESBD, a Malta-based higher education institution, recognised for career advancement and professional growth." },
    { title: "Global Webinars", text: "Networking opportunity with expert leaders and global professionals shaping the world." },
    { title: "Pathway to a Degree", text: "Opportunity to articulate into advanced degree programmes." },
  ],
  ctaHeading: "Ready to Lead with Agility?",
  ctaParagraph: "Enrol today and become a certified agile thought leader.",
  iconMap: {
    globe: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="1.5" />
        <path d="M2 12h20" stroke="white" strokeWidth="1.5" />
      </svg>
    ),
    clock: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path
          d="M12 6v6l4 2"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="1.5" />
      </svg>
    ),
    people: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path
          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2M15 7a3 3 0 11-6 0 3 3 0 016 0z"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),
    clipboard: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),
    bolt: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path
          d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"
          stroke="white"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      </svg>
    ),
    cap: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path
          d="M12 14l9-5-9-5-9 5 9 5zm0 0v7"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    briefcase: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <path
          d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
};

const womensLeadership = {
  heroTitleEm: "Certified Women's Leadership & Gender Changemaker",
  programmeDetails: [
    { label: "School", value: "Faculty of Business & Management" },
    { label: "Start Date", value: "October 2026" },
    { label: "Certificate", value: "Professional Certificate" },
    { label: "Fee", value: "\u20ac750" },
    { label: "Credits", value: "N/A" },
    { label: "Language", value: "English" },
    { label: "Duration", value: "6-12 Months" },
    { label: "Mode", value: "Distance Learning" },
  ],
  overviewParagraphs: [
    "The Certified Women's Leadership & Gender Changemaker Programme offers a unique opportunity to earn the esteemed Certified Women's Leadership & Gender Changemaker designation.",
    "This programme focuses on women's leadership and gender advocacy, combining rigorous academic study with practical application. Designed for women leaders in diverse sectors, it fosters transformative leadership, critical thinking, and the ability to implement innovative solutions for pressing gender equity challenges.",
    "Through a blend of theoretical frameworks, global perspectives, and real-world application, this programme prepares graduates to lead impactful change in their organisations and communities.",
  ],
  overviewImageAlt: "Women's Leadership",
  highlights: [
    {
      title: "Live Learning Experience",
      text: "Engage in immersive live webinar sessions to enhance your learning experience with direct, real-time interaction.",
      icon: "globe",
    },
    {
      title: "Expert Guest Lecturers",
      text: "Learn from distinguished guest lecturers and certified professionals who bring real-world insights to the classroom.",
      icon: "clock",
    },
    {
      title: "Practical, Real-World Knowledge",
      text: "Gain hands-on leadership and gender advocacy experience to apply directly in your professional environment.",
      icon: "people",
    },
    {
      title: "Certified Recognition",
      text: "Earn the prestigious Certified Women's Leadership & Gender Changemaker designation upon completion.",
      icon: "clipboard",
    },
    {
      title: "Global Networking Opportunities",
      text: "Collaborate with peers and expand your network of professionals and mentors worldwide.",
      icon: "bolt",
    },
    {
      title: "Online Contact Hours with Programme Leader",
      text: "Benefit from direct interaction with the programme leader through weekly tutorials, monthly lectures, and revision guidance.",
      icon: "cap",
    },
  ],
  modules: [
    {
      title: "Foundations of Leadership & Gender Theory",
      desc: "Explore foundational leadership theories through a gender lens, examining how gender shapes leadership styles, opportunities, and barriers across organisational and societal contexts.",
    },
    {
      title: "Women in Strategic Leadership & Decision-Making",
      desc: "Develop strategic leadership capabilities with a focus on women's representation in boardrooms, executive roles, and policy-making, including negotiation, influence, and organisational power dynamics.",
    },
    {
      title: "Gender Equity, Policy & Advocacy",
      desc: "Analyse global gender equity frameworks, policy instruments, and advocacy strategies. Examine how legislation, institutional reform, and grassroots movements drive meaningful change for women and marginalised communities.",
    },
    {
      title: "Communication, Influence & Personal Branding for Women Leaders",
      desc: "Master persuasive communication, public speaking, executive presence, and personal branding strategies tailored for women navigating leadership in corporate, political, and non-profit sectors.",
    },
    {
      title: "Leading Change & Building Inclusive Organisations",
      desc: "Explore change management methodologies with an emphasis on building diverse, equitable, and inclusive workplaces. Develop practical skills to lead cultural transformation within organisations.",
    },
    {
      title: "Capstone: Applied Leadership & Gender Changemaker Project",
      desc: "Apply accumulated knowledge to a real-world leadership or gender advocacy project, demonstrating strategic thinking, research capability, stakeholder engagement, and measurable impact.",
    },
  ],
  careersIntro:
    "Graduates are prepared for leadership roles in gender advocacy and women's empowerment",
  careersIconKey: "briefcase",
  careers: [
    {
      title: "Senior Leadership Roles",
      text: "Pursue senior leadership positions in corporate, government, and international organisations with enhanced strategic capabilities.",
    },
    {
      title: "Gender Advocacy Specialist",
      text: "Advocate for gender equality in corporate, non-profit, and policy environments, positioning yourself as a champion for inclusive change.",
    },
    {
      title: "Diversity & Inclusion Manager",
      text: "Lead organisational DEI strategies, programmes, and cultural transformation initiatives across sectors.",
    },
    {
      title: "Women's Empowerment Programme Director",
      text: "Design and manage empowerment programmes for women in education, business, and community development.",
    },
    {
      title: "Policy Advisor on Gender Affairs",
      text: "Provide expert guidance to governments and institutions on gender-responsive policy development and implementation.",
    },
    {
      title: "Social Enterprise Leader",
      text: "Launch or lead social enterprises focused on women's economic empowerment, education, and community development.",
    },
  ],
  keyAttractions: [
    { title: "Hands-On Leadership Workshops", text: "Engage in practical sessions that build your leadership skills." },
    { title: "Eligibility for Summer School Programmes in Europe", text: "Exclusive access to immersive summer programmes." },
    {
      title: "Professional Certification",
      text: "Earn a professional certificate issued by ESBD, a Malta-based higher education institution. This programme is delivered as a Continuing Professional Development (CPD) certificate, recognised for career advancement and professional growth.",
    },
    { title: "Practical, Real-World Knowledge", text: "Apply leadership skills with real-world experience to apply directly in your professional environment." },
    { title: "Pathway to a Degree", text: "Opportunity to articulate into advanced degree programmes." },
  ],
  ctaHeading: "Ready to Lead Change?",
  ctaParagraph: "Enrol today and become a certified women's leadership and gender changemaker.",
  iconMap: {
    globe: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="1.5" />
        <path d="M2 12h20" stroke="white" strokeWidth="1.5" />
      </svg>
    ),
    clock: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path
          d="M12 6v6l4 2"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="1.5" />
      </svg>
    ),
    people: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path
          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2M15 7a3 3 0 11-6 0 3 3 0 016 0z"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),
    clipboard: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),
    bolt: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path
          d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"
          stroke="white"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      </svg>
    ),
    cap: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path
          d="M12 14l9-5-9-5-9 5 9 5zm0 0v7"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    briefcase: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <path
          d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
};

export const certifiedDesign2CourseData = {
  "business-consultant": businessConsultant,
  "data-analyst": dataAnalyst,
  "executive-career-coach": executiveCareerCoach,
  "agile-thought-leader": agileThoughtLeader,
  "womens-leadership-gender": womensLeadership,
};

